/**
 * LERA Smart Selector — Core (Sprint 8.3C.3)
 * State, events, catalog resolution, extension hooks.
 * No UI — architecture only.
 */
window.LeraSmartSelector = window.LeraSmartSelector || {};

(function (LSS) {
  const EVENT_NAME = 'variant:selected';
  const LEGACY_EVENT_NAME = 'lera:variant:selected';

  const HOOK_NAMES = [
    'searchProvider',
    'favorites',
    'recentModels',
    'brandFilters',
    'compatibilitySearch',
  ];

  /**
   * @typedef {Object} LeraSmartSelectorState
   * @property {Object|null} variant
   * @property {number|null} variantId
   * @property {{id:number,handle:string,title:string}} product
   * @property {string} selectorMode
   * @property {string} renderMode
   * @property {number} variantCount
   * @property {number} threshold
   * @property {Array<{name:string,value:string,optionValueId:string}>} selectedOptions
   * @property {string|null} sectionId
   */

  LSS.EVENT = EVENT_NAME;
  LSS.LEGACY_EVENT = LEGACY_EVENT_NAME;

  LSS.createState = function createState(config = {}) {
    return {
      variant: config.variant ?? null,
      variantId: config.variant?.id ?? config.variantId ?? null,
      product: config.product ?? { id: null, handle: '', title: '' },
      selectorMode: config.selectorMode ?? 'automatic',
      renderMode: config.renderMode ?? 'button',
      variantCount: Number(config.variantCount) || 0,
      threshold: Number(config.threshold) || 12,
      selectedOptions: Array.isArray(config.selectedOptions) ? config.selectedOptions.slice() : [],
      sectionId: config.sectionId ?? null,
    };
  };

  LSS.readCatalog = function readCatalog(root) {
    try {
      const node = root?.querySelector?.('[data-lera-variant-catalog]');
      return node ? JSON.parse(node.textContent) : [];
    } catch {
      return [];
    }
  };

  LSS.readSelectedVariantNode = function readSelectedVariantNode(variantRoot) {
    try {
      const node = variantRoot?.querySelector?.('[data-selected-variant]');
      return node ? JSON.parse(node.textContent) : null;
    } catch {
      return null;
    }
  };

  LSS.readSelectedOptionsFromDOM = function readSelectedOptionsFromDOM(variantRoot) {
    if (!variantRoot) return [];

    const selections = [];

    variantRoot.querySelectorAll('fieldset, .product-form__input--dropdown').forEach((group) => {
      const checked =
        group.querySelector('input:checked') ||
        group.querySelector('select option[selected]') ||
        group.querySelector('select option:checked');

      if (!checked) return;

      selections.push({
        name: checked.dataset.optionName || '',
        value: checked.value || '',
        optionValueId: checked.dataset.optionValueId || '',
      });
    });

    return selections;
  };

  LSS.resolveVariantFromSelections = function resolveVariantFromSelections(variants, selectedOptions) {
    if (!Array.isArray(variants) || !variants.length || !selectedOptions?.length) return null;

    return (
      variants.find((variant) => {
        const variantValues = [variant.option1, variant.option2, variant.option3].filter(Boolean);
        if (variantValues.length !== selectedOptions.length) return false;
        return selectedOptions.every((selection, index) => variantValues[index] === selection.value);
      }) || null
    );
  };

  LSS.resolveVariant = function resolveVariant(variants, selectedOptions, fallbackVariant) {
    return (
      LSS.resolveVariantFromSelections(variants, selectedOptions) ||
      fallbackVariant ||
      null
    );
  };

  LSS.patchState = function patchState(state, partial) {
    const next = Object.assign({}, state, partial);
    if (partial.variant !== undefined) {
      next.variantId = partial.variant?.id ?? null;
    } else if (partial.variantId !== undefined) {
      next.variantId = partial.variantId;
    }
    return next;
  };

  LSS.createEventDetail = function createEventDetail(state, previousVariantId) {
    return {
      variant: state.variant,
      variantId: state.variantId,
      product: state.product,
      selectorMode: state.selectorMode,
      renderMode: state.renderMode,
      variantCount: state.variantCount,
      threshold: state.threshold,
      selectedOptions: state.selectedOptions.slice(),
      previousVariantId: previousVariantId ?? null,
      source: 'lera-smart-selector',
    };
  };

  LSS.dispatchVariantSelected = function dispatchVariantSelected(host, detail) {
    if (!host) return;

    const eventInit = { bubbles: true, detail };
    host.dispatchEvent(new CustomEvent(EVENT_NAME, eventInit));
    document.dispatchEvent(new CustomEvent(LEGACY_EVENT_NAME, eventInit));
  };

  class LeraSmartSelectorHooks {
    constructor() {
      this._providers = Object.fromEntries(HOOK_NAMES.map((name) => [name, null]));
    }

    register(name, provider) {
      if (!Object.prototype.hasOwnProperty.call(this._providers, name)) return false;
      this._providers[name] = typeof provider === 'function' ? provider : null;
      return true;
    }

    get(name) {
      return this._providers[name] ?? null;
    }

    invoke(name, ...args) {
      const provider = this.get(name);
      return provider ? provider(...args) : null;
    }

    list() {
      return HOOK_NAMES.slice();
    }
  }

  LSS.Hooks = LeraSmartSelectorHooks;
  LSS.hooks = new LeraSmartSelectorHooks();

  const listeners = new Set();

  LSS.onVariantSelected = function onVariantSelected(callback) {
    if (typeof callback !== 'function') return () => {};
    listeners.add(callback);
    return () => listeners.delete(callback);
  };

  LSS._notifyVariantSelected = function _notifyVariantSelected(detail) {
    listeners.forEach((callback) => {
      try {
        callback(detail);
      } catch (error) {
        console.error('[LERA Smart Selector]', error);
      }
    });
  };
})(window.LeraSmartSelector);
